// client/src/coms/views/chatview_parts/ChatComposer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { comsApiGet } from '../../comsApi';
import { emojiResolver } from '../../customMedia';
import StickerPicker from './StickerPicker';
import StickerSuggestionBar, { STICKER_SUGGESTION_LIST_ID, suggestionOptionId, useStickerSuggestions } from './StickerSuggestions';
import useStickerSuggestionsEnabled from '../../stickers/stickerSuggestPrefs';
import { tokenForSticker } from '../../stickers/localStickerRelay';
import { removeTriggerWord } from '../../stickers/stickerSuggest';

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function safeFirstUrlFromText(text) {
  const s = String(text || '');
  const m = s.match(/https?:\/\/[^\s<>()]+/i);
  if (!m || !m[0]) return '';
  const raw = String(m[0]).trim();
  try {
    return new URL(raw).toString();
  } catch {
    return raw;
  }
}

function classifyMediaUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  if (!u) return { kind: '', url: '' };

  const clean = u.split('#')[0].split('?')[0];

  const isImage =
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(clean) ||
    clean.includes('i.imgur.com/') ||
    clean.includes('media.discordapp.net/') ||
    clean.includes('cdn.discordapp.com/');

  const isVideo = /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(clean);
  const isAudio = /\.(mp3|wav|ogg|m4a|aac|flac|opus)$/i.test(clean);

  const isYouTube =
    clean.includes('youtube.com/watch') ||
    clean.includes('youtu.be/') ||
    clean.includes('youtube.com/shorts/');

  if (isImage) return { kind: 'image', url };
  if (isVideo) return { kind: 'video', url };
  if (isAudio) return { kind: 'audio', url };
  if (isYouTube) return { kind: 'youtube', url };
  return { kind: '', url: '' };
}

function firstNonEmptyString(values) {
  for (const value of Array.isArray(values) ? values : []) {
    const s = String(value || '').trim();
    if (s) return s;
  }
  return '';
}

function pickGifPreviewUrl(item) {
  const m = item && typeof item === 'object' ? item : {};
  const images = m.images && typeof m.images === 'object' ? m.images : {};
  const mediaFormats = m.media_formats && typeof m.media_formats === 'object' ? m.media_formats : {};
  const media = Array.isArray(m.media) ? m.media : [];

  return firstNonEmptyString([
    images.fixed_width_small && images.fixed_width_small.url,
    images.fixed_width && images.fixed_width.url,
    images.preview_gif && images.preview_gif.url,
    images.original && images.original.url,
    mediaFormats.tinygif && mediaFormats.tinygif.url,
    mediaFormats.gif && mediaFormats.gif.url,
    mediaFormats.mediumgif && mediaFormats.mediumgif.url,
    mediaFormats.nanogif && mediaFormats.nanogif.url,
    media[0] && media[0].gif && media[0].gif.url,
    media[0] && media[0].tinygif && media[0].tinygif.url,
    m.preview_url,
    m.previewUrl,
    m.thumbnail,
    m.thumb,
    m.url,
  ]);
}

function pickGifContentUrl(item) {
  const m = item && typeof item === 'object' ? item : {};
  const images = m.images && typeof m.images === 'object' ? m.images : {};
  const mediaFormats = m.media_formats && typeof m.media_formats === 'object' ? m.media_formats : {};
  const media = Array.isArray(m.media) ? m.media : [];

  return firstNonEmptyString([
    images.original && images.original.url,
    images.fixed_width && images.fixed_width.url,
    images.preview_gif && images.preview_gif.url,
    mediaFormats.gif && mediaFormats.gif.url,
    mediaFormats.mediumgif && mediaFormats.mediumgif.url,
    mediaFormats.tinygif && mediaFormats.tinygif.url,
    media[0] && media[0].gif && media[0].gif.url,
    media[0] && media[0].mediumgif && media[0].mediumgif.url,
    media[0] && media[0].tinygif && media[0].tinygif.url,
    m.content_url,
    m.contentUrl,
    m.gif_url,
    m.gifUrl,
    m.url,
  ]);
}

function normalizeGifItem(item, index) {
  const m = item && typeof item === 'object' ? item : {};
  const url = pickGifContentUrl(m);
  const previewUrl = pickGifPreviewUrl(m) || url;
  const title = firstNonEmptyString([m.title, m.content_description, m.caption, m.name, m.id, `GIF ${index + 1}`]);
  const id = firstNonEmptyString([m.id, m.uuid, url, previewUrl, String(index + 1)]);

  if (!url || !previewUrl) return null;

  return {
    id,
    title,
    url,
    previewUrl,
  };
}

function normalizeGifPayload(payload) {
  const root = payload && typeof payload === 'object' ? payload : {};
  const candidates = []
    .concat(
      Array.isArray(root.data) ? root.data : [],
      Array.isArray(root.results) ? root.results : [],
      Array.isArray(root.items) ? root.items : [],
      Array.isArray(root.gifs) ? root.gifs : [],
      Array.isArray(root.stickers) ? root.stickers : []
    );

  const out = [];
  const seen = new Set();

  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizeGifItem(candidates[i], i);
    if (!normalized) continue;
    const key = normalized.url || normalized.previewUrl || normalized.id;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }

  return out;
}

function buildGifApiUrl(mode, query) {
  const q = String(query || '').trim();
  const params = new URLSearchParams();
  params.set('limit', '18');
  if (q) params.set('q', q);

  if (mode === 'search') return `/api/coms/media/gifs/search?${params.toString()}`;
  return `/api/coms/media/gifs/trending?${params.toString()}`;
}

function kindLabel(kind) {
  const k = String(kind || '').trim().toLowerCase();
  if (k === 'image') return 'Image';
  if (k === 'video') return 'Video';
  if (k === 'audio') return 'Audio';
  return 'File';
}

function fileNameFromClipboardFile(file, index) {
  const f = file instanceof File ? file : null;
  const rawName = f ? String(f.name || '').trim() : '';
  if (rawName) return rawName;

  const type = String((f && f.type) || '').trim().toLowerCase();
  const ext = type.startsWith('image/')
    ? type.split('/')[1] || 'png'
    : type.startsWith('audio/')
      ? type.split('/')[1] || 'bin'
      : type.startsWith('video/')
        ? type.split('/')[1] || 'bin'
        : 'bin';

  return `pasted-file-${index + 1}.${ext.replace(/[^a-z0-9]+/gi, '') || 'bin'}`;
}

function extractFilesFromClipboardEvent(e) {
  const dt = e && e.clipboardData ? e.clipboardData : null;
  if (!dt) return [];

  const out = [];
  const seen = new Set();

  const items = Array.from(dt.items || []);
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item || item.kind !== 'file') continue;
    const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
    if (!(file instanceof File)) continue;

    const normalized = new File([file], fileNameFromClipboardFile(file, out.length), {
      type: file.type || 'application/octet-stream',
      lastModified: Date.now(),
    });

    const key = `${normalized.name}::${normalized.type}::${normalized.size}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }

  if (!out.length) {
    const files = Array.from(dt.files || []);
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (!(file instanceof File)) continue;

      const normalized = new File([file], fileNameFromClipboardFile(file, out.length), {
        type: file.type || 'application/octet-stream',
        lastModified: Date.now(),
      });

      const key = `${normalized.name}::${normalized.type}::${normalized.size}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(normalized);
    }
  }

  return out;
}

function normalizeMentionMembers(members) {
  const arr = safeArray(members);
  const out = [];
  const seen = new Set();

  for (let i = 0; i < arr.length; i += 1) {
    const item = arr[i] && typeof arr[i] === 'object' ? arr[i] : {};
    const id = item.user_id != null ? item.user_id : item.id != null ? item.id : item.member_id != null ? item.member_id : '';
    const display = firstNonEmptyString([
      item.display_name,
      item.server_display_name,
      item.nickname,
      item.nick,
      item.name,
      item.username,
      item.handle,
    ]);
    const username = firstNonEmptyString([item.username, item.handle, item.name, display]);

    if (!display) continue;

    const key = `${String(id || '').trim()}::${display.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({
      id: String(id || display),
      label: display,
      username,
      search: `${display} ${username}`.trim().toLowerCase(),
      mentionText: `@[${display}]`,
      kind: 'member',
    });
  }

  return out;
}

function getActiveMention(value, caretPos) {
  const text = String(value == null ? '' : value);
  const caret = Number.isFinite(caretPos) ? Math.max(0, Math.min(text.length, caretPos)) : text.length;
  const before = text.slice(0, caret);

  const bracketAtIndex = before.lastIndexOf('@[');
  if (bracketAtIndex >= 0) {
    const bracketToken = before.slice(bracketAtIndex);
    if (/^@\[[^\]\n]*$/.test(bracketToken)) {
      if (bracketAtIndex > 0) {
        const prev = before[bracketAtIndex - 1];
        if (/\S/.test(prev) && !/\s/.test(prev) && prev !== '(' && prev !== '[' && prev !== '{') return null;
      }

      return {
        start: bracketAtIndex,
        end: caret,
        query: bracketToken.slice(2),
        raw: bracketToken,
        bracketed: true,
      };
    }
  }

  const atIndex = before.lastIndexOf('@');
  if (atIndex < 0) return null;

  if (atIndex > 0) {
    const prev = before[atIndex - 1];
    if (/\S/.test(prev) && !/\s/.test(prev) && prev !== '(' && prev !== '[' && prev !== '{') return null;
  }

  const token = before.slice(atIndex);
  if (!/^@[A-Za-z0-9_]*$/.test(token)) return null;

  return {
    start: atIndex,
    end: caret,
    query: token.slice(1),
    raw: token,
    bracketed: false,
  };
}

export default function ChatComposer({
  isShellMobile,
  canType,
  showEmojiPicker,
  setShowEmojiPicker,
  emojiButtonRef,
  emojiPopoverRef,
  inputRef,
  draft,
  setDraft,
  onKeyDown,
  placeholder,
  mobileComposerCompact,
  sending,
  send,
  insertIntoDraftAtCursor,
  inputBarStyle,
  pendingQuote,
  clearPendingQuote,
  pendingUploads = [],
  removePendingUpload,
  onPickFiles,
  uploading = false,
  clearPendingUploads,
  mentionMembers = [],
  allowEveryoneMention = true,
  fontOptions = [],
  selectedFont = 'inherit',
  onChangeFont,
  enableFontPicker = true,
  serverKey = '',
  onSendSticker,
  onOpenStickerManager,
  // Channel permissions (the server enforces them; these only shape the UI).
  canAttach = true,
  canSendStickers = true,
  // Sticker suggestions: this server's packs need Send stickers, all others Use external stickers.
  canSendServerStickers = true,
  canSendExternalStickers = true,
}) {
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [customEmojis, setCustomEmojis] = useState(null);
  const stickerButtonRef = useRef(null);
  const stickerPopoverRef = useRef(null);

  // Server emojis from every server I am in, loaded when the emoji picker first opens.
  useEffect(() => {
    if (!showEmojiPicker || customEmojis) return;
    let alive = true;
    comsApiGet('/me/emojis')
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r && r.emojis) ? r.emojis : [];
        emojiResolver.prime(list);
        setCustomEmojis(list);
      })
      .catch(() => alive && setCustomEmojis([]));
    return () => {
      alive = false;
    };
  }, [showEmojiPicker, customEmojis]);

  const customPickerData = useMemo(() => {
    const list = Array.isArray(customEmojis) ? customEmojis : [];
    if (!list.length) return { categories: [], tokens: {} };
    const toEntry = (e) => ({ id: `vox_${e.id}`, name: e.name, keywords: [e.name, e.server_name || ''], skins: [{ src: e.url }] });
    const byServer = new Map();
    for (const e of list) {
      const key = String(e.server_id);
      if (!byServer.has(key)) byServer.set(key, { id: `vox_server_${key}`, name: e.server_name || 'Server', emojis: [] });
      byServer.get(key).emojis.push(toEntry(e));
    }
    const favs = list.filter((e) => e.favorite);
    const categories = [...(favs.length ? [{ id: 'vox_favourites', name: 'Favourites', emojis: favs.map((e) => ({ ...toEntry(e), id: `vox_fav_${e.id}` })) }] : []), ...byServer.values()];
    const tokens = {};
    for (const e of list) {
      tokens[`vox_${e.id}`] = e.token;
      tokens[`vox_fav_${e.id}`] = e.token;
    }
    return { categories, tokens };
  }, [customEmojis]);

  useEffect(() => {
    if (!showStickerPicker) return undefined;
    const onDown = (e) => {
      const pop = stickerPopoverRef.current;
      const btn = stickerButtonRef.current;
      if (pop && pop.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      setShowStickerPicker(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowStickerPicker(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showStickerPicker]);
  const [gifQuery, setGifQuery] = useState('');
  const [gifLoading, setGifLoading] = useState(false);
  const [gifError, setGifError] = useState('');
  const [stickerError, setStickerError] = useState('');
  const [gifItems, setGifItems] = useState([]);
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const [mentionState, setMentionState] = useState(null);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [caret, setCaret] = useState(0);
  const suggestionsEnabled = useStickerSuggestionsEnabled();
  const [showMobileTools, setShowMobileTools] = useState(false);
  const [showMobileFontList, setShowMobileFontList] = useState(false);
  const [showDesktopFontPicker, setShowDesktopFontPicker] = useState(false);

  const gifButtonRef = useRef(null);
  const gifPopoverRef = useRef(null);
  const gifSearchInputRef = useRef(null);
  const gifFetchAbortRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const uploadInfoRef = useRef(null);
  const mentionPopoverRef = useRef(null);
  const desktopFontButtonRef = useRef(null);
  const desktopFontPopoverRef = useRef(null);
  const mobileToolsButtonRef = useRef(null);
  const mobileToolsPopoverRef = useRef(null);

  function toggleWrapSelection(textareaEl, wrapper) {
    if (!textareaEl) return false;

    const wrap = String(wrapper || '');
    if (!wrap) return false;

    const value = String(textareaEl.value == null ? '' : textareaEl.value);
    const selStart = typeof textareaEl.selectionStart === 'number' ? textareaEl.selectionStart : value.length;
    const selEnd = typeof textareaEl.selectionEnd === 'number' ? textareaEl.selectionEnd : value.length;

    const a = Math.min(selStart, selEnd);
    const b = Math.max(selStart, selEnd);
    const selected = value.slice(a, b);

    if (!selected) {
      const next = value.slice(0, a) + wrap + wrap + value.slice(b);
      setDraft(next);

      requestAnimationFrame(() => {
        try {
          textareaEl.focus();
          const pos = a + wrap.length;
          textareaEl.setSelectionRange(pos, pos);
        } catch {}
      });

      return true;
    }

    const leftStart = a - wrap.length;
    const rightEnd = b + wrap.length;

    const hasLeft = leftStart >= 0 && value.slice(leftStart, a) === wrap;
    const hasRight = rightEnd <= value.length && value.slice(b, rightEnd) === wrap;

    if (hasLeft && hasRight) {
      const next = value.slice(0, leftStart) + selected + value.slice(rightEnd);
      setDraft(next);

      requestAnimationFrame(() => {
        try {
          textareaEl.focus();
          textareaEl.setSelectionRange(leftStart, leftStart + selected.length);
        } catch {}
      });

      return true;
    }

    const next = value.slice(0, a) + wrap + selected + wrap + value.slice(b);
    setDraft(next);

    requestAnimationFrame(() => {
      try {
        textareaEl.focus();
        textareaEl.setSelectionRange(a + wrap.length, b + wrap.length);
      } catch {}
    });

    return true;
  }

  function isStrikeShortcut(e) {
    const key = String(e.key || '').toLowerCase();
    const code = String(e.code || '');

    const hasCmdOrCtrl = !!(e.ctrlKey || e.metaKey);
    const hasShift = !!e.shiftKey;
    const altOnlyCombo = !!e.altKey && !e.ctrlKey && !e.metaKey;

    const cmdCtrlShiftX = hasCmdOrCtrl && hasShift && (code === 'KeyX' || key === 'x');
    const cmdCtrlShift5 =
      hasCmdOrCtrl && hasShift && (code === 'Digit5' || key === '5' || key === '%' || key === '(');
    const altShift5 = altOnlyCombo && hasShift && (code === 'Digit5' || key === '5' || key === '%' || key === '(');

    return cmdCtrlShiftX || cmdCtrlShift5 || altShift5;
  }

  function tryHandleStrikeShortcut(e) {
    if (!canType) return false;
    if (!isStrikeShortcut(e)) return false;

    const el = (inputRef && inputRef.current) || e.currentTarget;
    if (!el) return false;

    e.preventDefault();
    e.stopPropagation();

    return toggleWrapSelection(el, '~~');
  }

  function refreshMentionStateFromTextarea(textareaEl, nextValue) {
    const el = textareaEl || (inputRef && inputRef.current);
    const value = String(nextValue == null ? draft : nextValue);
    const caret = el && typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const active = getActiveMention(value, caret);
    setMentionState(active);
    setCaret(caret);
  }

  function closeMentionPicker() {
    setMentionState(null);
    setMentionHighlightIndex(0);
  }

  function closeMobileTools(focusInput = false) {
    setShowMobileTools(false);
    setShowMobileFontList(false);
    if (focusInput) {
      requestAnimationFrame(() => {
        try {
          if (inputRef && inputRef.current) inputRef.current.focus();
        } catch {}
      });
    }
  }

  function closeDesktopFontPicker(focusInput = false) {
    setShowDesktopFontPicker(false);
    if (focusInput) {
      requestAnimationFrame(() => {
        try {
          if (inputRef && inputRef.current) inputRef.current.focus();
        } catch {}
      });
    }
  }

  function toggleDesktopFontPicker() {
    if (!canType) return;
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    setShowUploadInfo(false);
    closeMentionPicker();
    closeMobileTools(false);
    setShowDesktopFontPicker((prev) => !prev);
  }

  function selectDesktopFont(value) {
    if (typeof onChangeFont === 'function') onChangeFont(value);
    closeDesktopFontPicker(true);
  }

  function closeAllTransientPopovers({ focusInput = false } = {}) {
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    setShowUploadInfo(false);
    closeMentionPicker();
    closeMobileTools(false);
    setShowDesktopFontPicker(false);

    if (focusInput) {
      requestAnimationFrame(() => {
        try {
          if (inputRef && inputRef.current) inputRef.current.focus();
        } catch {}
      });
    }
  }

  function onComposerKeyDownCapture(e) {
    if (tryHandleStrikeShortcut(e)) return;
  }

  const normalizedMentionMembers = useMemo(() => normalizeMentionMembers(mentionMembers), [mentionMembers]);

  const normalizedFontOptions = useMemo(() => {
    const arr = safeArray(fontOptions)
      .map((item) => {
        const opt = item && typeof item === 'object' ? item : {};
        const value = String(opt.value || '').trim();
        const label = String(opt.label || value || '').trim();
        if (!value || !label) return null;
        return { value, label };
      })
      .filter(Boolean);

    if (!arr.length) {
      return [{ value: 'inherit', label: 'Default' }];
    }

    return arr;
  }, [fontOptions]);

  const normalizedSelectedFont = useMemo(() => {
    const raw = String(selectedFont || '').trim() || 'inherit';
    return normalizedFontOptions.some((opt) => opt.value === raw) ? raw : normalizedFontOptions[0].value;
  }, [selectedFont, normalizedFontOptions]);

  const selectedFontLabel = useMemo(() => {
    const match = normalizedFontOptions.find((opt) => opt.value === normalizedSelectedFont);
    return match ? match.label : 'Default';
  }, [normalizedFontOptions, normalizedSelectedFont]);

  const mentionItems = useMemo(() => {
    if (!mentionState) return [];

    const q = String(mentionState.query || '').trim().toLowerCase();
    const items = [];

    if (!mentionState.bracketed && allowEveryoneMention && 'everyone'.includes(q || 'everyone')) {
      items.push({
        id: '__everyone__',
        label: 'everyone',
        username: 'Ping all members',
        search: 'everyone',
        mentionText: '@everyone',
        kind: 'everyone',
      });
    }

    const filtered = normalizedMentionMembers.filter((item) => {
      if (!q) return true;
      return item.search.includes(q) || String(item.label || '').toLowerCase().startsWith(q);
    });

    return items.concat(filtered.slice(0, 25));
  }, [mentionState, normalizedMentionMembers, allowEveryoneMention]);

  useEffect(() => {
    if (!mentionItems.length) {
      setMentionHighlightIndex(0);
      return;
    }
    if (mentionHighlightIndex >= mentionItems.length) {
      setMentionHighlightIndex(0);
    }
  }, [mentionItems, mentionHighlightIndex]);

  const stickerSuggestions = useStickerSuggestions({
    enabled: suggestionsEnabled && typeof onSendSticker === 'function' && canSendStickers && canType,
    draft,
    caret,
    serverKey,
    canSendServer: canSendServerStickers,
    canSendExternal: canSendExternalStickers,
    blocked: sending || (!!mentionState && mentionItems.length > 0) || showStickerPicker || showEmojiPicker || showGifPicker,
  });

  useEffect(() => {
    if (!stickerError) return undefined;
    const timer = setTimeout(() => setStickerError(''), 4500);
    return () => clearTimeout(timer);
  }, [stickerError]);

  // Server stickers send their token; a local sticker's image is relayed first (localStickerRelay.js).
  async function sendStickerItem(sticker) {
    if (!sticker || typeof onSendSticker !== 'function') return false;
    let token;
    try {
      token = await tokenForSticker(sticker);
    } catch (e) {
      setStickerError(String((e && e.data && e.data.error) || (e && e.message) || 'Could not send that sticker.'));
      return false;
    }
    return onSendSticker(token);
  }

  // Sends the sticker on its own. Only the trigger word is removed from the draft, and only after the
  // send succeeded; anything else the user typed stays in the composer.
  async function pickStickerSuggestion(sticker) {
    const target = stickerSuggestions.target;
    if (!sticker || !target || typeof onSendSticker !== 'function') return;
    const ok = await sendStickerItem(sticker);
    if (ok !== true) return;
    let nextCaret = 0;
    setDraft((prev) => {
      const r = removeTriggerWord(prev, target);
      nextCaret = r.caret;
      return r.text;
    });
    requestAnimationFrame(() => {
      try {
        const el = inputRef && inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
        setCaret(nextCaret);
      } catch {}
    });
  }

  const stickerSuggestionActive = stickerSuggestions.open && stickerSuggestions.highlight >= 0 ? stickerSuggestions.items[stickerSuggestions.highlight] : null;
  const stickerAria = stickerSuggestions.open
    ? { 'aria-controls': STICKER_SUGGESTION_LIST_ID, 'aria-activedescendant': stickerSuggestionActive ? suggestionOptionId(stickerSuggestionActive) : undefined }
    : {};

  function insertMentionChoice(item) {
    const mention = mentionState;
    const el = inputRef && inputRef.current;
    if (!item || !mention) return;

    const value = String(draft == null ? '' : draft);
    const before = value.slice(0, mention.start);
    const after = value.slice(mention.end);
    const mentionText = String(item.mentionText || '').trim();
    const needsSpaceAfter = after ? !/^\s/.test(after) : true;
    const next = `${before}${mentionText}${needsSpaceAfter ? ' ' : ''}${after}`;

    setDraft(next);
    closeMentionPicker();
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    setShowUploadInfo(false);
    closeMobileTools(false);

    requestAnimationFrame(() => {
      try {
        if (el) {
          const caret = before.length + mentionText.length + (needsSpaceAfter ? 1 : 0);
          el.focus();
          el.setSelectionRange(caret, caret);
        }
      } catch {}
    });
  }

  function onComposerKeyDown(e) {
    if (tryHandleStrikeShortcut(e)) return;

    if (mentionState && mentionItems.length) {
      const key = String(e.key || '');

      if (key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex((prev) => {
          const max = mentionItems.length - 1;
          return prev >= max ? 0 : prev + 1;
        });
        return;
      }

      if (key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex((prev) => {
          const max = mentionItems.length - 1;
          return prev <= 0 ? max : prev - 1;
        });
        return;
      }

      if (key === 'Enter' || key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        insertMentionChoice(mentionItems[Math.max(0, Math.min(mentionHighlightIndex, mentionItems.length - 1))]);
        return;
      }

      if (key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeMentionPicker();
        return;
      }
    }

    if (stickerSuggestions.handleKeyDown(e, pickStickerSuggestion)) return;

    if (typeof onKeyDown === 'function') onKeyDown(e);
  }

  function onComposerPaste(e) {
    if (!canType || uploading || !canAttach) return;

    const files = extractFilesFromClipboardEvent(e);
    if (!files.length) return;

    e.preventDefault();
    e.stopPropagation();

    closeAllTransientPopovers();

    if (typeof onPickFiles === 'function') onPickFiles(files);
  }

  function closeGifPicker(focusInput = true) {
    setShowGifPicker(false);
    if (focusInput) {
      requestAnimationFrame(() => {
        try {
          if (inputRef && inputRef.current) inputRef.current.focus();
        } catch {}
      });
    }
  }

  function openGifPicker() {
    setShowEmojiPicker(false);
    setShowUploadInfo(false);
    closeMentionPicker();
    closeMobileTools(false);
    setShowDesktopFontPicker(false);
    setShowGifPicker(true);
    requestAnimationFrame(() => {
      try {
        if (gifSearchInputRef.current) gifSearchInputRef.current.focus();
      } catch {}
    });
  }

  function insertGifUrl(url) {
    const value = String(url || '').trim();
    if (!value) return;
    const suffix = draft && /\S$/.test(String(draft)) ? ' ' : '';
    insertIntoDraftAtCursor(`${suffix}${value} `);
    closeGifPicker(true);
  }

  function onEmojiSelect(emoji) {
    const custom = emoji && emoji.id ? customPickerData.tokens[String(emoji.id)] : '';
    if (custom) {
      insertIntoDraftAtCursor(`${custom} `);
      return;
    }
    const ch = emoji && emoji.native ? String(emoji.native) : '';
    if (!ch) return;
    insertIntoDraftAtCursor(ch);
  }

  function triggerFilePicker() {
    if (!canType || uploading || !canAttach) return;
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    closeMentionPicker();
    closeMobileTools(false);
    setShowDesktopFontPicker(false);
    setShowUploadInfo((prev) => !prev);
  }

  function openFilePickerDirect() {
    if (!canAttach) return;
    setShowUploadInfo(false);
    closeMobileTools(false);
    setShowDesktopFontPicker(false);
    try {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
    } catch {}
  }

  function onFileInputChange(e) {
    const files = e && e.target && e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    setShowUploadInfo(false);
    if (typeof onPickFiles === 'function') onPickFiles(files);
    try {
      if (e.target) e.target.value = '';
    } catch {}
  }

  function openEmojiPicker() {
    setShowGifPicker(false);
    setShowUploadInfo(false);
    closeMentionPicker();
    closeMobileTools(false);
    setShowDesktopFontPicker(false);
    setShowEmojiPicker((v) => !v);
    setTimeout(() => {
      try {
        if (inputRef.current) inputRef.current.focus();
      } catch {}
    }, 0);
  }

  function openGifPickerFromMobileTools() {
    closeMobileTools(false);
    openGifPicker();
  }

  function openEmojiPickerFromMobileTools() {
    closeMobileTools(false);
    setShowGifPicker(false);
    setShowUploadInfo(false);
    closeMentionPicker();
    setShowDesktopFontPicker(false);
    setShowEmojiPicker(true);
    setTimeout(() => {
      try {
        if (inputRef.current) inputRef.current.focus();
      } catch {}
    }, 0);
  }

  function selectFontFromMobile(value) {
    if (typeof onChangeFont === 'function') onChangeFont(value);
    closeAllTransientPopovers({ focusInput: true });
  }

  useEffect(() => {
    if (!showGifPicker && gifFetchAbortRef.current) {
      try {
        gifFetchAbortRef.current.abort();
      } catch {}
      gifFetchAbortRef.current = null;
    }
  }, [showGifPicker]);

  useEffect(() => {
    if (!showGifPicker) return undefined;

    const q = String(gifQuery || '').trim();
    const mode = q ? 'search' : 'trending';

    const timer = setTimeout(async () => {
      if (gifFetchAbortRef.current) {
        try {
          gifFetchAbortRef.current.abort();
        } catch {}
      }

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      gifFetchAbortRef.current = controller;

      setGifLoading(true);
      setGifError('');

      try {
        const res = await fetch(buildGifApiUrl(mode, q), {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          signal: controller ? controller.signal : undefined,
        });

        if (!res.ok) {
          throw new Error(`GIF search failed (${res.status})`);
        }

        const payload = await res.json().catch(() => null);
        const normalized = normalizeGifPayload(payload);

        setGifItems(normalized);
        setGifError(normalized.length ? '' : q ? 'No GIFs found.' : 'No trending GIFs found.');
      } catch (err) {
        const aborted = err && (err.name === 'AbortError' || String(err.message || '').toLowerCase().includes('abort'));
        if (aborted) return;
        setGifItems([]);
        setGifError('GIF search is not ready yet.');
      } finally {
        setGifLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [showGifPicker, gifQuery]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    function onPointerDown(e) {
      const target = e.target;

      const insideEmoji =
        (emojiPopoverRef && emojiPopoverRef.current && emojiPopoverRef.current.contains(target)) ||
        (emojiButtonRef && emojiButtonRef.current && emojiButtonRef.current.contains(target));

      const insideGif =
        (gifPopoverRef && gifPopoverRef.current && gifPopoverRef.current.contains(target)) ||
        (gifButtonRef && gifButtonRef.current && gifButtonRef.current.contains(target));

      const insideUpload =
        (uploadInfoRef.current && uploadInfoRef.current.contains(target)) ||
        (uploadButtonRef.current && uploadButtonRef.current.contains(target));

      const insideMention =
        (mentionPopoverRef.current && mentionPopoverRef.current.contains(target)) ||
        (inputRef && inputRef.current && inputRef.current.contains(target));

      const insideDesktopFont =
        (desktopFontPopoverRef.current && desktopFontPopoverRef.current.contains(target)) ||
        (desktopFontButtonRef.current && desktopFontButtonRef.current.contains(target));

      const insideMobileTools =
        (mobileToolsPopoverRef.current && mobileToolsPopoverRef.current.contains(target)) ||
        (mobileToolsButtonRef.current && mobileToolsButtonRef.current.contains(target));

      if (!insideGif && showGifPicker) setShowGifPicker(false);
      if (!insideEmoji && showEmojiPicker) setShowEmojiPicker(false);
      if (!insideUpload && showUploadInfo) setShowUploadInfo(false);
      if (!insideMention && mentionState) closeMentionPicker();
      if (!insideDesktopFont && showDesktopFontPicker) setShowDesktopFontPicker(false);
      if (!insideMobileTools && showMobileTools) closeMobileTools(false);
    }

    function onKeyEscape(e) {
      if (String(e.key || '') !== 'Escape') return;
      if (showGifPicker) {
        e.preventDefault();
        setShowGifPicker(false);
      }
      if (showUploadInfo) {
        e.preventDefault();
        setShowUploadInfo(false);
      }
      if (showDesktopFontPicker) {
        e.preventDefault();
        setShowDesktopFontPicker(false);
      }
      if (mentionState) {
        e.preventDefault();
        closeMentionPicker();
      }
      if (showMobileTools) {
        e.preventDefault();
        closeMobileTools(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyEscape, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyEscape, true);
    };
  }, [
    showEmojiPicker,
    setShowEmojiPicker,
    showGifPicker,
    showUploadInfo,
    emojiPopoverRef,
    emojiButtonRef,
    mentionState,
    inputRef,
    showDesktopFontPicker,
    showMobileTools,
  ]);

  const quoteObj = pendingQuote && typeof pendingQuote === 'object' ? pendingQuote : null;
  const quoteAuthor = quoteObj ? String(quoteObj.author || '').trim() : '';
  const quoteStamp = quoteObj ? String(quoteObj.stamp || '').trim() : '';
  const quoteText = quoteObj ? String(quoteObj.text || '').trim() : '';
  const quoteTop = quoteObj ? String(quoteObj.header || '').trim() : '';

  const quoteLine = quoteTop || (quoteAuthor && quoteStamp ? `${quoteAuthor} • ${quoteStamp}` : quoteAuthor || quoteStamp || 'Quote');

  const urlInQuote = quoteObj ? safeFirstUrlFromText(quoteText) : '';
  const media = classifyMediaUrl(urlInQuote);

  const quoteSnippet = quoteText
    ? quoteText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(Boolean).slice(0, 2).join(' · ')
    : '';

  const gifGridItems = useMemo(() => gifItems, [gifItems]);
  const gifAttributionLabel = 'Powered by Klipy';

  const normalizedPendingUploads = useMemo(() => {
    return safeArray(pendingUploads).map((item) => ({
      id: item && item.id != null ? item.id : '',
      media_kind: String((item && item.media_kind) || 'file').trim() || 'file',
      public_url: String((item && item.public_url) || '').trim(),
      thumb_url: String((item && item.thumb_url) || '').trim(),
      original_name: String((item && item.original_name) || '').trim() || 'Upload',
    }));
  }, [pendingUploads]);

  const mobileToolButtonStyle = {
    minHeight: 34,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
    color: 'inherit',
    cursor: canType ? 'pointer' : 'default',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
  };

  const gifPickerMaxWidth = isShellMobile ? 'calc(100vw - 16px)' : 640;
  const gifScrollMaxHeight = isShellMobile ? 240 : 300;
  const gifColumns = isShellMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))';
  const gifThumbAspect = '1 / 1';

  return (
    <div className="coms-inputbar" style={inputBarStyle}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={onFileInputChange}
        style={{ display: 'none' }}
      />

      {showEmojiPicker ? (
        <div
          ref={emojiPopoverRef}
          style={{
            position: 'absolute',
            right: isShellMobile ? 8 : 0,
            bottom: 'calc(100% + 8px)',
            zIndex: 3000,
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            maxWidth: isShellMobile ? 'calc(100vw - 16px)' : undefined,
          }}
        >
          <Picker
            data={data}
            theme={typeof document !== 'undefined' && document.documentElement.getAttribute('data-vx-theme') === 'light' ? 'light' : 'dark'}
            previewPosition="none"
            searchPosition="sticky"
            navPosition="top"
            skinTonePosition="search"
            perLine={9}
            maxFrequentRows={2}
            emojiSize={30}
            emojiButtonSize={42}
            custom={customPickerData.categories}
            onEmojiSelect={onEmojiSelect}
          />
        </div>
      ) : null}

      {showGifPicker ? (
        <div
          ref={gifPopoverRef}
          style={{
            position: 'absolute',
            left: isShellMobile ? 8 : 0,
            right: isShellMobile ? 8 : 'auto',
            width: isShellMobile ? 'auto' : 'min(640px, calc(100vw - 32px))',
            maxWidth: gifPickerMaxWidth,
            bottom: 'calc(100% + 8px)',
            zIndex: 3001,
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 10px 8px',
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                ref={gifSearchInputRef}
                type="text"
                value={gifQuery}
                onChange={(e) => setGifQuery(e.target.value)}
                placeholder="Search KLIPY"
                style={{
                  width: '100%',
                  minWidth: 0,
                  height: 34,
                  borderRadius: 8,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                  color: 'inherit',
                  outline: 'none',
                  padding: '0 10px',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
              <div
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  lineHeight: 1.2,
                  opacity: 0.62,
                  paddingLeft: 2,
                  userSelect: 'none',
                }}
              >
                {gifAttributionLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={() => closeGifPicker(true)}
              style={{
                height: 34,
                padding: '0 10px',
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
                flexShrink: 0,
                alignSelf: 'flex-start',
              }}
            >
              Close
            </button>
          </div>

          <div
            style={{
              height: gifScrollMaxHeight,
              maxHeight: gifScrollMaxHeight,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: 8,
              scrollbarGutter: 'stable',
            }}
          >
            {gifLoading ? (
              <div style={{ padding: '18px 10px', fontSize: 13, opacity: 0.78 }}>Loading GIFs…</div>
            ) : gifError ? (
              <div
                style={{
                  padding: '22px 12px',
                  fontSize: 13,
                  opacity: 0.82,
                  textAlign: 'center',
                  borderRadius: 10,
                  border: '1px dashed rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.92 }}>
                  {gifError}
                </div>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.58 }}>
                  {gifAttributionLabel}
                </div>
              </div>
            ) : gifGridItems.length ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: gifColumns,
                  gap: 6,
                }}
              >
                {gifGridItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => insertGifUrl(item.url)}
                    style={{
                      padding: 0,
                      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      display: 'block',
                      minWidth: 0,
                      width: '100%',
                      height: 'auto',
                      minHeight: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        background: 'rgba(0,0,0,0.22)',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={item.previewUrl}
                        alt={item.title}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: '22px 12px',
                  fontSize: 13,
                  opacity: 0.82,
                  textAlign: 'center',
                  borderRadius: 10,
                  border: '1px dashed rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.92 }}>
                  No GIFs found.
                </div>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.58 }}>
                  {gifAttributionLabel}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showUploadInfo ? (
        <div
          ref={uploadInfoRef}
          style={{
            position: 'absolute',
            left: isShellMobile ? 8 : 0,
            bottom: 'calc(100% + 8px)',
            zIndex: 3002,
            width: isShellMobile ? 'calc(100vw - 16px)' : 360,
            maxWidth: 'calc(100vw - 16px)',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Upload rules
          </div>

          <div
            style={{
              padding: 12,
              fontSize: 12,
              lineHeight: 1.55,
              opacity: 0.9,
            }}
          >
            <div>Images: up to 20MB</div>
            <div>Audio: up to 10MB, auto-deletes after 7 days</div>
            <div>Videos: up to 20MB, max 10 seconds, auto-delete after 3 days</div>
            <div>Other files: zip only, up to 10MB, auto-delete after 24 hours</div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: 12,
              borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            }}
          >
            <button
              type="button"
              onClick={openFilePickerDirect}
              style={{
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              Choose files
            </button>

            <button
              type="button"
              onClick={() => setShowUploadInfo(false)}
              style={{
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      {stickerError ? (
        <div role="alert" style={{ position: 'absolute', left: 0, right: 0, bottom: 'calc(100% + 8px)', zIndex: 3003, padding: '8px 12px', borderRadius: 10, background: 'rgba(80,20,24,0.96)', border: '1px solid rgba(255,138,140,0.4)', color: '#ffd7d8', fontSize: 13 }}>
          {stickerError}
        </div>
      ) : null}

      {stickerSuggestions.open && !(mentionState && mentionItems.length) ? (
        <StickerSuggestionBar
          items={stickerSuggestions.items}
          target={stickerSuggestions.target}
          highlight={stickerSuggestions.highlight}
          onHighlight={stickerSuggestions.setHighlight}
          onPick={pickStickerSuggestion}
          onDismiss={stickerSuggestions.dismiss}
          isShellMobile={isShellMobile}
        />
      ) : null}

      {mentionState && mentionItems.length ? (
        <div
          ref={mentionPopoverRef}
          style={{
            position: 'absolute',
            left: isShellMobile ? 8 : 0,
            right: isShellMobile ? 8 : 'auto',
            bottom: 'calc(100% + 8px)',
            zIndex: 3003,
            width: isShellMobile ? 'calc(100vw - 16px)' : 320,
            maxWidth: 'calc(100vw - 16px)',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              padding: '8px 10px',
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              fontSize: 11,
              fontWeight: 800,
              opacity: 0.7,
              userSelect: 'none',
            }}
          >
            Mention someone
          </div>

          <div
            style={{
              maxHeight: 260,
              overflowY: 'auto',
              padding: 6,
            }}
          >
            {mentionItems.map((item, index) => {
              const active = index === mentionHighlightIndex;
              return (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMentionChoice(item);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    borderRadius: 9,
                    background: active ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: item.kind === 'everyone' ? 'rgba(var(--vx-link-rgb, 110,160,255), 0.22)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                      fontSize: 13,
                      fontWeight: 800,
                    }}
                  >
                    {item.kind === 'everyone' ? '@' : String(item.label || '?').slice(0, 1).toUpperCase()}
                  </div>

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.kind === 'everyone' ? '@everyone' : `@[${item.label}]`}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        opacity: 0.6,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.kind === 'everyone' ? 'Ping all members' : item.username || 'Member'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {isShellMobile && showMobileTools ? (
        <div
          ref={mobileToolsPopoverRef}
          style={{
            position: 'absolute',
            left: 8,
            right: 8,
            bottom: 'calc(100% + 8px)',
            zIndex: 3004,
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
              Message tools
            </div>

            <button
              type="button"
              onClick={() => closeMobileTools(true)}
              style={{
                height: 28,
                padding: '0 10px',
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Close
            </button>
          </div>

          {!showMobileFontList ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 8,
                padding: 10,
              }}
            >
              <button
                type="button"
                disabled={!canType}
                onClick={openEmojiPickerFromMobileTools}
                style={{
                  ...mobileToolButtonStyle,
                  padding: '0 12px',
                }}
              >
                😀 Emoji
              </button>

              <button
                type="button"
                disabled={!canType}
                onClick={openGifPickerFromMobileTools}
                style={{
                  ...mobileToolButtonStyle,
                  padding: '0 12px',
                }}
              >
                GIF
              </button>

              <button
                type="button"
                disabled={!canType || uploading || !canAttach}
                title={canAttach ? undefined : 'You do not have permission to attach files here'}
                onClick={openFilePickerDirect}
                style={{
                  ...mobileToolButtonStyle,
                  padding: '0 12px',
                }}
              >
                + Upload
              </button>

              {enableFontPicker ? (
                <button
                  type="button"
                  disabled={!canType}
                  onClick={() => setShowMobileFontList(true)}
                  style={{
                    ...mobileToolButtonStyle,
                    padding: '0 12px',
                  }}
                >
                  Aa Font
                </button>
              ) : null}

              {enableFontPicker ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    padding: '4px 2px 0',
                    fontSize: 11,
                    opacity: 0.62,
                  }}
                >
                  Current font: {selectedFontLabel}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ padding: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
                  Choose font
                </div>

                <button
                  type="button"
                  onClick={() => setShowMobileFontList(false)}
                  style={{
                    height: 28,
                    padding: '0 10px',
                    borderRadius: 8,
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Back
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                {normalizedFontOptions.map((opt) => {
                  const active = opt.value === normalizedSelectedFont;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={!canType}
                      onClick={() => selectFontFromMobile(opt.value)}
                      style={{
                        minHeight: 34,
                        borderRadius: 10,
                        border: active ? '1px solid rgba(var(--vx-link-rgb, 110,160,255), 0.45)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                        background: active ? 'rgba(var(--vx-link-rgb, 110,160,255), 0.14)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                        color: 'inherit',
                        cursor: canType ? 'pointer' : 'default',
                        fontSize: 13,
                        fontWeight: active ? 800 : 700,
                        textAlign: 'left',
                        padding: '0 12px',
                        fontFamily: opt.value === 'inherit' ? 'inherit' : opt.value,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {quoteObj ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 10,
              padding: '6px 8px',
              marginBottom: 6,
              borderRadius: 10,
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            }}
          >
            <div
              style={{
                width: 3,
                borderRadius: 999,
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.25 * var(--vx-ov, 1)))',
                flexShrink: 0,
              }}
            />

            {media && media.kind ? (
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                  background: 'rgba(0,0,0,0.25)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
                title={
                  media.kind === 'image'
                    ? 'Image'
                    : media.kind === 'video'
                      ? 'Video'
                      : media.kind === 'audio'
                        ? 'Audio'
                        : 'YouTube'
                }
              >
                {media.kind === 'image' ? (
                  <img
                    src={media.url}
                    alt="quoted"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => {
                      try {
                        e.currentTarget.style.display = 'none';
                      } catch {}
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 16, opacity: 0.9 }}>
                    {media.kind === 'youtube' ? '▶' : media.kind === 'audio' ? '🔊' : '🎞'}
                  </span>
                )}

                {media.kind === 'video' || media.kind === 'youtube' ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.18)',
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.35)',
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.20 * var(--vx-ov, 1)))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                      }}
                    >
                      ▶
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Quoting: {quoteLine}
              </div>

              {!media.kind && quoteSnippet ? (
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.75,
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {quoteSnippet}
                </div>
              ) : media.kind ? (
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                  {media.kind === 'image' ? 'Image' : media.kind === 'video' ? 'Video' : media.kind === 'audio' ? 'Audio' : 'YouTube'}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => clearPendingQuote && clearPendingQuote()}
              disabled={!canType}
              title="Clear quote"
              aria-label="Clear quote"
              style={{
                width: 28,
                height: 28,
                padding: 0,
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: canType ? 'pointer' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        ) : null}

        {normalizedPendingUploads.length ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 6,
            }}
          >
            {normalizedPendingUploads.map((item) => (
              <div
                key={String(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  maxWidth: '100%',
                  padding: '6px 8px',
                  borderRadius: 10,
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                    background: 'rgba(0,0,0,0.2)',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.media_kind === 'image' && item.public_url ? (
                    <img
                      src={item.public_url}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : item.media_kind === 'video' && item.thumb_url ? (
                    <img
                      src={item.thumb_url}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <span style={{ fontSize: 14, opacity: 0.9 }}>
                      {item.media_kind === 'image'
                        ? '🖼'
                        : item.media_kind === 'video'
                          ? '🎞'
                          : item.media_kind === 'audio'
                            ? '🔊'
                            : '📄'}
                    </span>
                  )}
                </div>

                <div style={{ minWidth: 0, maxWidth: isShellMobile ? 160 : 220 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.original_name}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.65 }}>
                    {kindLabel(item.media_kind)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removePendingUpload && removePendingUpload(item.id)}
                  style={{
                    width: 22,
                    height: 22,
                    padding: 0,
                    borderRadius: 7,
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                    color: 'inherit',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  title="Remove upload"
                  aria-label="Remove upload"
                >
                  ✕
                </button>
              </div>
            ))}

            {normalizedPendingUploads.length > 1 ? (
              <button
                type="button"
                onClick={() => clearPendingUploads && clearPendingUploads()}
                style={{
                  height: 34,
                  padding: '0 10px',
                  borderRadius: 8,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Clear all
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            width: '100%',
            gap: isShellMobile ? 6 : 0,
          }}
        >
          {isShellMobile ? (
            <>
              <button
                ref={mobileToolsButtonRef}
                type="button"
                disabled={!canType}
                onClick={() => {
                  setShowEmojiPicker(false);
                  setShowGifPicker(false);
                  setShowUploadInfo(false);
                  closeMentionPicker();
                  setShowMobileTools((prev) => {
                    const next = !prev;
                    if (!next) setShowMobileFontList(false);
                    return next;
                  });
                }}
                title="Tools"
                aria-label="Open message tools"
                style={{
                  width: 30,
                  minWidth: 30,
                  height: 30,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                  background: showMobileTools ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                  cursor: canType ? 'pointer' : 'default',
                  fontSize: 18,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                +
              </button>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDraft(next);
                    refreshMentionStateFromTextarea(e.target, next);
                  }}
                  onPaste={onComposerPaste}
                  onKeyDownCapture={onComposerKeyDownCapture}
                  onKeyDown={onComposerKeyDown}
                  onClick={(e) => refreshMentionStateFromTextarea(e.target)}
                  onKeyUp={(e) => refreshMentionStateFromTextarea(e.target)}
                  onSelect={(e) => refreshMentionStateFromTextarea(e.target)}
                  {...stickerAria}
                  placeholder={placeholder}
                  disabled={!canType}
                  rows={1}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    minHeight: mobileComposerCompact ? 34 : 30,
                    fontSize: 16,
                    resize: 'none',
                    border: 'none',
                    outline: 'none',
                    margin: 0,
                    background: 'transparent',
                    padding: '7px 0 5px',
                    lineHeight: '22px',
                    overflow: 'hidden',
                    fontFamily: normalizedSelectedFont === 'inherit' ? 'inherit' : normalizedSelectedFont,
                  }}
                />
              </div>

              <button
                type="button"
                className="vx-send-btn"
                disabled={!canType || sending || uploading || (!draft.trim() && !quoteObj && !normalizedPendingUploads.length)}
                onClick={send}
                style={{
                  height: 32,
                  padding: '0 10px',
                  marginLeft: 6,
                  flexShrink: 0,
                }}
              >
                {sending ? 'Sending…' : uploading ? 'Uploading…' : 'Send'}
              </button>
            </>
          ) : (
            <>
              <button
                ref={emojiButtonRef}
                type="button"
                disabled={!canType}
                onClick={openEmojiPicker}
                title="Emoji"
                aria-label="Open emoji picker"
                style={{
                  width: 28,
                  minWidth: 28,
                  height: 28,
                  padding: 0,
                  marginRight: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                  background: showEmojiPicker ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                  cursor: canType ? 'pointer' : 'default',
                  fontSize: 16,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                😀
              </button>

              <button
                ref={gifButtonRef}
                type="button"
                disabled={!canType}
                onClick={() => {
                  if (showGifPicker) {
                    closeGifPicker(true);
                  } else {
                    openGifPicker();
                  }
                }}
                title="GIFs"
                aria-label="Open GIF picker"
                style={{
                  height: 28,
                  minWidth: 40,
                  padding: '0 8px',
                  marginRight: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                  background: showGifPicker ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                  cursor: canType ? 'pointer' : 'default',
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1,
                  flexShrink: 0,
                  letterSpacing: 0.2,
                }}
              >
                GIF
              </button>

              {typeof onSendSticker === 'function' && canSendStickers ? (
                <div style={{ position: 'relative', marginRight: 8, flexShrink: 0 }}>
                  <button
                    ref={stickerButtonRef}
                    type="button"
                    disabled={!canType}
                    onClick={() => {
                      setShowEmojiPicker(false);
                      setShowGifPicker(false);
                      setShowStickerPicker((v) => !v);
                    }}
                    title="Stickers"
                    aria-label="Open sticker picker"
                    style={{
                      height: 28,
                      minWidth: 32,
                      padding: '0 6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 6,
                      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                      background: showStickerPicker ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                      cursor: canType ? 'pointer' : 'default',
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                  >
                    🗒️
                  </button>
                  {showStickerPicker ? (
                    <div
                      ref={stickerPopoverRef}
                      style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 10px)',
                        left: 0,
                        zIndex: 3000,
                        borderRadius: 12,
                        overflow: 'hidden',
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                      }}
                    >
                      <StickerPicker
                        serverKey={serverKey}
                        canSendServer={canSendServerStickers}
                        canSendExternal={canSendExternalStickers}
                        onPick={(sticker) => sendStickerItem(sticker)}
                        onClose={() => setShowStickerPicker(false)}
                        onManage={
                          typeof onOpenStickerManager === 'function'
                            ? () => {
                                setShowStickerPicker(false);
                                onOpenStickerManager();
                              }
                            : undefined
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {enableFontPicker ? (
                <div
                  style={{
                    position: 'relative',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                >
                  <button
                    ref={desktopFontButtonRef}
                    type="button"
                    disabled={!canType}
                    onClick={toggleDesktopFontPicker}
                    title="Message font"
                    aria-label="Choose message font"
                    aria-haspopup="listbox"
                    aria-expanded={showDesktopFontPicker ? 'true' : 'false'}
                    style={{
                      height: 28,
                      maxWidth: 150,
                      padding: '0 8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      borderRadius: 6,
                      border: showDesktopFontPicker ? '1px solid rgba(var(--vx-link-rgb, 110,160,255), 0.45)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                      background: showDesktopFontPicker ? 'rgba(var(--vx-link-rgb, 110,160,255), 0.14)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                      color: 'inherit',
                      cursor: canType ? 'pointer' : 'default',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      flexShrink: 0,
                      outline: 'none',
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: normalizedSelectedFont === 'inherit' ? 'inherit' : normalizedSelectedFont,
                      }}
                    >
                      {selectedFontLabel}
                    </span>
                    <span style={{ opacity: 0.72, fontSize: 10 }}>▼</span>
                  </button>

                  {showDesktopFontPicker ? (
                    <div
                      ref={desktopFontPopoverRef}
                      role="listbox"
                      aria-label="Message font options"
                      style={{
                        position: 'absolute',
                        right: 0,
                        bottom: 'calc(100% + 8px)',
                        width: 220,
                        maxHeight: 260,
                        overflowY: 'auto',
                        padding: 8,
                        zIndex: 3002,
                        borderRadius: 12,
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                        background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                      }}
                    >
                      <div
                        style={{
                          padding: '2px 4px 8px',
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: 0.3,
                          opacity: 0.68,
                          textTransform: 'uppercase',
                        }}
                      >
                        Choose font
                      </div>

                      <div style={{ display: 'grid', gap: 6 }}>
                        {normalizedFontOptions.map((opt) => {
                          const active = opt.value === normalizedSelectedFont;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="option"
                              aria-selected={active ? 'true' : 'false'}
                              disabled={!canType}
                              onClick={() => selectDesktopFont(opt.value)}
                              style={{
                                minHeight: 34,
                                width: '100%',
                                borderRadius: 10,
                                border: active ? '1px solid rgba(var(--vx-link-rgb, 110,160,255), 0.45)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                                background: active ? 'rgba(var(--vx-link-rgb, 110,160,255), 0.14)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                                color: 'inherit',
                                cursor: canType ? 'pointer' : 'default',
                                fontSize: 13,
                                fontWeight: active ? 800 : 700,
                                textAlign: 'left',
                                padding: '0 12px',
                                fontFamily: opt.value === 'inherit' ? 'inherit' : opt.value,
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <button
                ref={uploadButtonRef}
                type="button"
                disabled={!canType || uploading}
                onClick={triggerFilePicker}
                title="Attach files"
                aria-label="Attach files"
                style={{
                  width: 28,
                  minWidth: 28,
                  height: 28,
                  padding: 0,
                  marginRight: 8,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
                  background: showUploadInfo ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
                  cursor: canType && !uploading ? 'pointer' : 'default',
                  fontSize: 16,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {uploading ? '…' : '+'}
              </button>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDraft(next);
                    refreshMentionStateFromTextarea(e.target, next);
                  }}
                  onPaste={onComposerPaste}
                  onKeyDownCapture={onComposerKeyDownCapture}
                  onKeyDown={onComposerKeyDown}
                  onClick={(e) => refreshMentionStateFromTextarea(e.target)}
                  onKeyUp={(e) => refreshMentionStateFromTextarea(e.target)}
                  onSelect={(e) => refreshMentionStateFromTextarea(e.target)}
                  {...stickerAria}
                  placeholder={placeholder}
                  disabled={!canType}
                  rows={1}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    minHeight: mobileComposerCompact ? 34 : 30,
                    fontSize: 18,
                    resize: 'none',
                    border: 'none',
                    outline: 'none',
                    margin: 0,
                    background: 'transparent',
                    padding: '6px 0 4px',
                    lineHeight: '24px',
                    overflow: 'hidden',
                    fontFamily: normalizedSelectedFont === 'inherit' ? 'inherit' : normalizedSelectedFont,
                  }}
                />
              </div>

              <button
                type="button"
                className="vx-send-btn"
                disabled={!canType || sending || uploading || (!draft.trim() && !quoteObj && !normalizedPendingUploads.length)}
                onClick={send}
                style={{
                  height: 30,
                  padding: '0 12px',
                  marginLeft: 8,
                  flexShrink: 0,
                }}
              >
                {sending ? 'Sending…' : uploading ? 'Uploading…' : 'Send'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}